import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import { authRoutes } from '@/modules/auth'
import { useWorkspaceStore } from '@/store/useWorkspaceStore'
import { APP_NAME } from '@/lib/constants'
import { pipelinesRoutes } from '@/router/crm/pipelines'
import { sequencesRoutes } from '@/router/crm/sequences'

declare module 'vue-router' {
  interface RouteMeta {
    requiresAuth?: boolean
    guestOnly?: boolean
    /** Authed route that must remain reachable without a selected workspace. */
    workspaceOptional?: boolean
    title?: string
    /** Workspace owner/admin only; others are sent to the dashboard. */
    adminOnly?: boolean
  }
}

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    redirect: () => {
      const storedUser = getStoredUser()
      return storedUser ? '/dashboard' : '/login'
    },
  },
  {
    path: '/dashboard',
    name: 'dashboard',
    component: () => import('@/views/DashboardView.vue'),
    meta: { requiresAuth: true, title: 'Dashboard' },
  },
  {
    path: '/ceo',
    name: 'ceo-dashboard',
    component: () => import('@/views/CeoDashboardView.vue'),
    meta: { requiresAuth: true, title: 'CEO Dashboard' },
  },
  {
    path: '/clients',
    name: 'clients',
    component: () => import('@/views/clients/ClientsView.vue'),
    meta: { requiresAuth: true, title: 'Clients' },
  },
  {
    path: '/clients/new',
    name: 'clients.create',
    component: () => import('@/views/clients/ClientCreateView.vue'),
    meta: { requiresAuth: true, title: 'New Client' },
  },
  {
    path: '/clients/:id/:section?',
    name: 'client-detail',
    component: () => import('@/views/clients/ClientDetailView.vue'),
    meta: { requiresAuth: true, title: 'Client' },
  },
  {
    path: '/clients/:id/edit',
    name: 'clients.edit',
    component: () => import('@/views/clients/ClientEditView.vue'),
    meta: { requiresAuth: true, title: 'Edit Client' },
  },
  {
    path: '/clients/:clientId/campaigns/new',
    name: 'client-campaign-new',
    component: () => import('@/views/clients/ClientCampaignFormView.vue'),
    meta: { requiresAuth: true, title: 'New Campaign' },
  },
  {
    path: '/clients/:clientId/campaigns/:campaignId/edit',
    name: 'client-campaign-edit',
    component: () => import('@/views/clients/ClientCampaignFormView.vue'),
    meta: { requiresAuth: true, title: 'Edit Campaign' },
  },
  {
    path: '/clients/:clientId/campaigns/:campaignId',
    name: 'client-campaign-detail',
    component: () => import('@/views/clients/ClientCampaignDetailView.vue'),
    meta: { requiresAuth: true, title: 'Campaign' },
  },
  {
    path: '/clients/:clientId/documents/new',
    name: 'client-document-new',
    component: () => import('@/views/clients/ClientDocumentEditorView.vue'),
    meta: { requiresAuth: true, title: 'New Document' },
  },
  {
    path: '/clients/:clientId/documents/:docId',
    name: 'client-document-edit',
    component: () => import('@/views/clients/ClientDocumentEditorView.vue'),
    meta: { requiresAuth: true, title: 'Document' },
  },
  {
    path: '/clients/:clientId/collateral/:docUuid/compose',
    name: 'client-collateral-compose',
    component: () => import('@/views/clients/CollateralComposerView.vue'),
    meta: { requiresAuth: true, title: 'Collateral Composer' },
  },
  ...pipelinesRoutes,
  ...sequencesRoutes,
  {
    path: '/collateral',
    name: 'collateral',
    component: () => import('@/views/collateral/CollateralView.vue'),
    meta: { requiresAuth: true, title: 'Collateral' },
  },
  {
    path: '/tasks',
    name: 'tasks',
    component: () => import('@/views/tasks/TasksView.vue'),
    meta: { requiresAuth: true, title: 'Tasks' },
  },
  {
    path: '/social',
    name: 'social',
    component: () => import('@/views/social/SocialView.vue'),
    meta: { requiresAuth: true, title: 'Social' },
  },
  {
    path: '/social/history',
    name: 'social-history',
    component: () => import('@/views/social/SocialHistoryView.vue'),
    meta: { requiresAuth: true, title: 'Social History' },
  },
  {
    path: '/integrations',
    name: 'integrations',
    component: () => import('@/views/integrations/IntegrationsView.vue'),
    meta: { requiresAuth: true, adminOnly: true, title: 'Integrations' },
  },
  {
    path: '/integrations/slack/callback',
    name: 'slack-callback',
    component: () => import('@/views/integrations/SlackCallbackView.vue'),
    meta: { requiresAuth: true, adminOnly: true, title: 'Connecting Slack…' },
  },
  {
    path: '/accountability',
    name: 'accountability',
    component: () => import('@/views/accountability/AccountabilityView.vue'),
    meta: { requiresAuth: true, title: 'Accountability' },
  },
  {
    path: '/brain',
    redirect: '/brain/ask',
  },
  {
    path: '/brain/:tab',
    name: 'brain',
    component: () => import('@/views/brain/BrainView.vue'),
    meta: { requiresAuth: true, title: 'Brain' },
  },
  {
    path: '/ai-usage',
    name: 'ai-usage',
    component: () => import('@/views/AiUsageView.vue'),
    meta: { requiresAuth: true, title: 'AI Usage' },
  },
  {
    path: '/ai-usage/:uuid',
    name: 'ai-usage-detail',
    component: () => import('@/views/AiUsageDetailView.vue'),
    meta: { requiresAuth: true, title: 'AI Usage Detail' },
  },
  {
    path: '/meetings',
    name: 'meetings',
    component: () => import('@/views/meetings/MeetingsView.vue'),
    meta: { requiresAuth: true, title: 'Meetings' },
  },
  {
    path: '/inbox',
    name: 'inbox',
    component: () => import('@/views/inbox/InboxView.vue'),
    meta: { requiresAuth: true, title: 'Inbox' },
  },
  // Legacy paths from before the workspaces (multi-tenancy) feature.
  { path: '/workspace/meetings', redirect: '/meetings' },
  { path: '/workspace/inbox', redirect: '/inbox' },
  {
    path: '/workspaces',
    redirect: '/workspaces/select',
  },
  {
    path: '/workspaces/select',
    name: 'workspace-select',
    component: () => import('@/views/workspaces/WorkspaceSelectView.vue'),
    meta: { requiresAuth: true, workspaceOptional: true, title: 'Select Workspace' },
  },
  {
    // Terminal state for a session load that failed for a reason other than
    // being signed out. requiresAuth is false so a still-broken API can't bounce
    // the user between this route and the guard that sent them here.
    path: '/session-error',
    name: 'session-error',
    component: () => import('@/views/SessionErrorView.vue'),
    meta: { requiresAuth: false, workspaceOptional: true, title: 'Connection Problem' },
  },
  {
    path: '/workspaces/invite/:token',
    name: 'workspace-invite',
    component: () => import('@/views/workspaces/WorkspaceInviteView.vue'),
    meta: { requiresAuth: false, workspaceOptional: true, title: 'Workspace Invitation' },
  },
  {
    path: '/settings',
    redirect: '/settings/account',
  },
  {
    path: '/settings/:section',
    name: 'settings',
    component: () => import('@/views/settings/SettingsView.vue'),
    meta: { requiresAuth: true, title: 'Settings' },
  },
  {
    path: '/help',
    redirect: '/help/getting-started',
  },
  {
    path: '/help/:article',
    name: 'help',
    component: () => import('@/views/help/HelpView.vue'),
    meta: { requiresAuth: true, title: 'Help Center' },
  },
  {
    path: '/invite',
    name: 'invite',
    component: () => import('@/views/InviteView.vue'),
    meta: { requiresAuth: false, title: 'Accept Invitation' },
  },
  {
    path: '/privacy',
    name: 'privacy',
    component: () => import('@/views/PrivacyPolicyView.vue'),
    meta: { title: 'Privacy Policy' },
  },
  {
    path: '/terms',
    name: 'terms',
    component: () => import('@/views/TermsView.vue'),
    meta: { title: 'Terms of Service' },
  },
  ...authRoutes,
  {
    path: '/print/:docId',
    name: 'collateral-print',
    component: () => import('@/views/PrintCollateralView.vue'),
    meta: { title: 'Generating PDF…' },
  },
  { path: '/:pathMatch(.*)*', redirect: '/' },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
})

function getStoredUser(): Record<string, unknown> | null {
  try {
    const raw = localStorage.getItem('user')
    if (!raw || raw === 'undefined' || raw === 'null') return null
    return JSON.parse(raw) as Record<string, unknown>
  } catch {
    localStorage.removeItem('user')
    return null
  }
}

router.beforeEach(async (to) => {
  const storedUser      = getStoredUser()
  const isAuthenticated = storedUser !== null

  if (to.meta.guestOnly && isAuthenticated) return '/dashboard'

  if (to.meta.requiresAuth && !isAuthenticated) {
    return { name: 'login', query: { redirect: to.fullPath } }
  }

  if (to.meta.requiresAuth && isAuthenticated) {
    const { useSessionStore } = await import('@/store/useSessionStore')
    const session = useSessionStore()
    if (!session.user) {
      const result = await session.fetchSession()
      // A failed session load used to fall through here. That let the app render
      // its full shell against a stale localStorage selection — looking healthy
      // while every request went out under an unverified workspace (LPD-2 AC 18).
      if (result === 'error') {
        return { name: 'session-error', query: { redirect: to.fullPath } }
      }
      if (!session.user) {
        session.clearSession()
        return { name: 'login' }
      }
    }

    // Every authed route (except explicitly workspace-optional ones) needs a
    // current workspace — the API rejects tenant routes without the header.
    // Optional routes (workspace-select) skip hydration: it would auto-pick a workspace for them.
    const workspace = useWorkspaceStore()
    if ((!to.meta.workspaceOptional || to.meta.adminOnly) && !workspace.listLoaded && session.user.workspaces !== undefined) {
      workspace.hydrateFrom(session.user)
    }
    if (!to.meta.workspaceOptional) {
      // Checks the selection resolves against the loaded list, not merely that
      // localStorage holds something — a revoked workspace must not open the app.
      if (!workspace.hasValidSelection) {
        return { name: 'workspace-select' }
      }
    }
    // Independent of workspaceOptional: an adminOnly route is gated either way.
    if (to.meta.adminOnly && !workspace.isWorkspaceAdmin) return '/dashboard'
  }

  return true
})

router.afterEach((to) => {
  const title = to.meta.title ? `${to.meta.title} - ${APP_NAME}` : APP_NAME
  document.title = title
  document.querySelector('meta[property="og:title"]')?.setAttribute('content', title)
  document.querySelector('meta[name="twitter:title"]')?.setAttribute('content', title)
})

router.onError((err, to) => {
  const isChunkError = /Failed to fetch dynamically imported module|ChunkLoadError/i.test(err.message)
  if (isChunkError) {
    window.location.href = to.fullPath
  }
})

export default router
